import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Menu, Plus, Edit2, Trash2, Save, X, GripVertical, Eye, EyeOff, Image } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import AdminSidebar from '../../components/AdminSidebar';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCategories = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    icon: '',
    order: 0,
    is_active: true
  });

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (isLoggedIn !== 'true') {
      navigate('/admin', { replace: true });
      return;
    }
    fetchCategories();
  }, [navigate]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories/all`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/admin/categories/${editingId}`, formData);
        toast.success('Category updated!');
      } else {
        await axios.post(`${API_URL}/api/admin/categories`, formData);
        toast.success('Category created!');
      }
      fetchCategories();
      resetForm();
    } catch (error) {
      console.error('Failed to save category:', error);
      toast.error('Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingId(category.id);
    setFormData({
      name: category.name,
      description: category.description || '',
      image_url: category.image_url || '',
      icon: category.icon || '',
      order: category.order || 0,
      is_active: category.is_active !== false
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/categories/${id}`);
      toast.success('Category deleted!');
      fetchCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error('Failed to delete category');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setShowAddForm(false);
    setFormData({
      name: '',
      description: '',
      image_url: '',
      icon: '',
      order: 0,
      is_active: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-maroon"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <AdminSidebar active="categories" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:ml-64">
        <header className="bg-white border-b border-gold/10 px-4 py-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden"
            >
              <Menu className="w-6 h-6" />
            </Button>
            <h1 className="text-xl font-heading font-bold text-maroon">Categories</h1>
          </div>
          <Button 
            onClick={() => setShowAddForm(true)}
            className="bg-maroon hover:bg-maroon-dark text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
        </header>

        <main className="p-4 md:p-6">
          {/* Add/Edit Form Modal */}
          {showAddForm && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl p-6 w-full max-w-md">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-heading font-semibold text-maroon">
                    {editingId ? 'Edit Category' : 'Add Category'}
                  </h2>
                  <Button variant="ghost" onClick={resetForm}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-maroon/70 mb-1">Name *</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Category name"
                      required
                      className="border-maroon/20"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-maroon/70 mb-1">Description</label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Short description"
                      className="border-maroon/20"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-maroon/70 mb-1">Image URL</label>
                    <Input
                      value={formData.image_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                      placeholder="https://..."
                      className="border-maroon/20"
                    />
                    {formData.image_url && (
                      <img src={formData.image_url} alt="Preview" className="mt-2 h-20 object-cover rounded" />
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-maroon/70 mb-1">Icon Name</label>
                    <Input
                      value={formData.icon}
                      onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                      placeholder="e.g., User, Home, Star"
                      className="border-maroon/20"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-maroon/70 mb-1">Order</label>
                      <Input
                        type="number"
                        value={formData.order}
                        onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                        className="border-maroon/20"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 pb-2">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-maroon">Active</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 bg-maroon hover:bg-maroon-dark text-white">
                      <Save className="w-4 h-4 mr-2" />
                      {editingId ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Categories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <div
                key={category.id}
                className={`bg-white rounded-xl overflow-hidden border ${
                  category.is_active ? 'border-gold/10' : 'border-gray-200 opacity-60'
                }`}
              >
                <div className="aspect-video relative overflow-hidden bg-cream">
                  {category.image_url ? (
                    <img
                      src={category.image_url}
                      alt={category.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <Image className="w-12 h-12 text-maroon/20" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1">
                    {!category.is_active && (
                      <span className="bg-gray-500 text-white text-xs px-2 py-1 rounded-full">
                        Hidden
                      </span>
                    )}
                    <span className="bg-maroon text-white text-xs px-2 py-1 rounded-full">
                      #{category.order}
                    </span>
                  </div>
                </div>

                <div className="p-4">
                  <h3 className="font-heading font-semibold text-maroon mb-1">{category.name}</h3>
                  <p className="text-sm text-maroon/60 line-clamp-2 mb-3">{category.description || 'No description'}</p>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(category)}
                      className="flex-1"
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(category.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {categories.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4">
                <Image className="w-8 h-8 text-gold/50" />
              </div>
              <h3 className="font-heading font-semibold text-maroon mb-2">No Categories</h3>
              <p className="text-maroon/60 mb-4">Create your first category to organize products</p>
              <Button onClick={() => setShowAddForm(true)} className="bg-maroon hover:bg-maroon-dark text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminCategories;
